// src/screens/KioskVehicleCatalogScreen.js
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { vehiclesAPI } from '../services/api';
import { AuthContext } from '../context/AuthContext';
import useKioskInactivity from '../hooks/useKioskInactivity';
import { SlidersHorizontal, X, ChevronRight } from 'lucide-react';

// Kiosk components
import KioskFiltersSidebar from '../components/kiosk/KioskFiltersSidebar';
import KioskVehicleCard from '../components/kiosk/KioskVehicleCard';
import SortDropdown from '../components/vehicles/SortDropdown';

const KioskVehicleCatalogScreen = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // 2-minute global inactivity timer
  useKioskInactivity();

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    searchTerm: '',
    type: '',
    maxMileage: '',
    priceRange: ''
  });

  const [sortBy, setSortBy] = useState('popular');

  // Check kiosk authentication
  useEffect(() => {
    if (!user || user.role !== 'kiosk') {
      navigate('/login');
      return;
    }
  }, [user, navigate]);

  // Build API query params from filters
  const buildParams = () => {
    const params = {};

    // Filter by kiosk's dealer inventory
    if (user?.dealer?._id) {
      params.dealerId = user.dealer._id;
    }

    if (filters.type) {
      params.type = filters.type;
    }

    if (filters.searchTerm) {
      params.search = filters.searchTerm;
    }

    if (filters.maxMileage) {
      params.maxMileage = filters.maxMileage;
    }

    // Handle price range
    if (filters.priceRange) {
      if (filters.priceRange === '100000+') {
        params.minPrice = 100000;
      } else if (filters.priceRange.includes('-')) {
        const [min, max] = filters.priceRange.split('-');
        if (min) params.minPrice = parseInt(min);
        if (max) params.maxPrice = parseInt(max);
      } else {
        const maxPrice = parseInt(filters.priceRange.replace(/\D/g, ''));
        if (!isNaN(maxPrice)) params.maxPrice = maxPrice;
      }
    }

    if (sortBy) {
      params.sortBy = sortBy;
    }

    return params;
  };

  // Fetch vehicles whenever filters or sort changes (debounced)
  useEffect(() => {
    let cancelled = false;
    const timeout = setTimeout(async () => {
      try {
        setLoading(true);
        setError('');
        const params = buildParams();
        const { data } = await vehiclesAPI.getAll(params);
        if (!cancelled) {
          setVehicles(data);
        }
      } catch (err) {
        console.error('Error fetching vehicles', err);
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load vehicles');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortBy]);

  const handleVehicleSelect = (vehicleId) => {
    navigate(`/kiosk/book/customer-info?vehicle=${vehicleId}`);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    setShowMobileFilters(false);
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
  };

  const dealerName = user?.dealer?.name || 'this dealership';

  return (
    <div className="flex flex-col h-full bg-app-bg-light dark:bg-app-bg">
      {/* Header Section */}
      <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary border-b-2 border-app-border-light dark:border-app-border">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-base mb-4">
            <span className="text-app-text-secondary-light dark:text-app-text-secondary">Kiosk</span>
            <ChevronRight className="w-5 h-5 text-app-text-secondary-light dark:text-app-text-secondary" />
            <span className="text-app-text-light dark:text-app-text font-semibold">Select Vehicle</span>
          </div>

          {/* Page Title & Count */}
          <div className="flex items-baseline gap-4 mb-6">
            <h1 className="text-3xl md:text-4xl font-bold text-app-text-light dark:text-app-text">
              Available Vehicles
            </h1>
            {!loading && (
              <span className="text-lg text-app-text-secondary-light dark:text-app-text-secondary">
                {vehicles.length} {vehicles.length === 1 ? 'vehicle' : 'vehicles'}
              </span>
            )}
          </div>

          {/* Mobile Filter Toggle Button */}
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="lg:hidden w-full bg-app-accent hover:bg-app-accent/90 text-white py-4 rounded-xl font-bold text-lg transition-all duration-200 flex items-center justify-center gap-3"
          >
            <SlidersHorizontal className="w-6 h-6" />
            Show Filters
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="lg:grid lg:grid-cols-[320px,1fr] lg:gap-8">
            {/* Filters Sidebar - Desktop */}
            <aside className="hidden lg:block sticky top-8 h-fit">
              <KioskFiltersSidebar
                filters={filters}
                onFilterChange={handleFilterChange}
                onApplyFilters={handleApplyFilters}
                dealerName={dealerName}
              />
            </aside>

            {/* Mobile Filter Drawer */}
            {showMobileFilters && (
              <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setShowMobileFilters(false)}>
                <div
                  className="absolute left-0 top-0 bottom-0 w-96 max-w-[90vw] bg-app-bg-light dark:bg-app-bg overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="sticky top-0 bg-app-bg-light dark:bg-app-bg border-b-2 border-app-border-light dark:border-app-border p-6 flex items-center justify-between z-10">
                    <h2 className="text-xl font-bold text-app-text-light dark:text-app-text">Filters</h2>
                    <button
                      onClick={() => setShowMobileFilters(false)}
                      className="p-2 hover:bg-app-bg-tertiary-light dark:hover:bg-app-bg-tertiary rounded-lg transition-colors"
                    >
                      <X className="w-6 h-6 text-app-text-light dark:text-app-text" />
                    </button>
                  </div>
                  <div className="p-6">
                    <KioskFiltersSidebar
                      filters={filters}
                      onFilterChange={handleFilterChange}
                      onApplyFilters={handleApplyFilters}
                      dealerName={dealerName}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Results Column */}
            <main className="space-y-6">
              {/* Sort Controls */}
              <div className="flex items-center justify-between bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl border-2 border-app-border-light dark:border-app-border p-5">
                <div className="text-base font-semibold text-app-text-light dark:text-app-text">
                  {loading ? 'Loading...' : `${vehicles.length} vehicles available`}
                </div>
                <SortDropdown sortBy={sortBy} onSortChange={handleSortChange} />
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-900/40 text-red-300 px-5 py-4 rounded-xl text-base font-medium">
                  {error}
                </div>
              )}

              {/* Loading */}
              {loading && (
                <div className="text-center py-20">
                  <div className="inline-block w-12 h-12 border-4 border-app-accent border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-app-text-secondary-light dark:text-app-text-secondary text-lg">
                    Loading vehicles...
                  </p>
                </div>
              )}

              {/* No Results */}
              {!loading && vehicles.length === 0 && (
                <div className="text-center py-20 bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl border-2 border-app-border-light dark:border-app-border">
                  <p className="text-app-text-light dark:text-app-text text-2xl font-bold mb-3">
                    No vehicles found
                  </p>
                  <p className="text-app-text-secondary-light dark:text-app-text-secondary text-lg mb-6">
                    Try adjusting your filters to see more results
                  </p>
                  <button
                    onClick={() => handleFilterChange({ searchTerm: '', type: '', maxMileage: '', priceRange: '' })}
                    className="bg-app-accent hover:bg-app-accent/90 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-200"
                  >
                    Reset Filters
                  </button>
                </div>
              )}

              {/* Vehicle Results */}
              {!loading && vehicles.length > 0 && (
                <div className="space-y-6">
                  {vehicles.map((vehicle) => (
                    <KioskVehicleCard
                      key={vehicle._id}
                      vehicle={vehicle}
                      onSelect={handleVehicleSelect}
                    />
                  ))}
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KioskVehicleCatalogScreen;
